import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Heading } from "@/components/heading";
import { Button } from "@/components/button";
import { Text } from "@/components/text";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import PageContainer from "@/components/layouts/PageContainer";
import ScoreConversionTableForm from "./components/ScoreConversionTableForm";
import { 
  useScoreConversionTableQuery, 
  useUpdateScoreConversionTableMutation 
} from "@/apis/score_conversion_tables";
import { useQueryGetExamPaper } from "@/apis/exam_papers/getExamPaper";
import type { IUpdateScoreConversionTableRequest } from "@/apis/score_conversion_tables/types";

const ScoreConversionTableEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const { data: scoreTable, isLoading, error } = useScoreConversionTableQuery(id || "");
  const updateMutation = useUpdateScoreConversionTableMutation();
  
  // Query exam paper to get total question count if linked
  const { data: examPaper } = useQueryGetExamPaper(scoreTable?.exam_paper_id || "", {
    enabled: !!scoreTable?.exam_paper_id
  });

  // Calculate total question count from exam paper sections
  const totalQuestions = React.useMemo(() => {
    if (!examPaper?.sections) return 0;
    
    return examPaper.sections.reduce((total, section) => {
      return total + (Array.isArray(section.question_ids) ? section.question_ids.length : 0);
    }, 0);
  }, [examPaper]);

  const handleSubmit = async (data: IUpdateScoreConversionTableRequest) => {
    if (!id) return;
    
    try {
      await updateMutation.mutateAsync({ id, data });
      navigate(`/score_conversion_tables/${id}`); // Navigate to detail page
    } catch (error) {
      console.error("Failed to update score conversion table:", error);
    }
  };

  const handleCancel = () => {
    navigate(-1); // Go back to previous page
  };

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center py-8 text-red-500">
          Invalid score conversion table ID.
        </div>
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer>
        <div className="text-center py-8">Loading score conversion table...</div>
      </PageContainer>
    );
  }

  if (error || !scoreTable) {
    return (
      <PageContainer>
        <div className="text-center py-8 text-red-500">
          Error loading score conversion table. Please try again.
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button plain onClick={() => navigate(-1)}>
            <ArrowLeftIcon className="h-5 w-5" />
            Back
          </Button>
          <div>
            <Heading>Edit Score Conversion Table</Heading>
      
            <Text className="text-gray-600">
              Update the score conversion table for converting raw scores to scale scores.
              {examPaper && (
                <span className="block mt-1 text-blue-600 font-medium">
                  Total Questions: {totalQuestions}
                </span>
              )}
            </Text>
          </div>
        </div>

        <ScoreConversionTableForm
          initialData={scoreTable}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={updateMutation.isPending}
          totalQuestions={totalQuestions}
          isEdit={true}
        />
      </div>
    </PageContainer>
  );
};

export default ScoreConversionTableEditPage; 